// ═══════════════════════════════════════════════════════════════════════════
// RISK MATRIX PAGE
// Migration S5.3 du plan TS+Vite (2026-05-10) : .js → .ts strict.
// ═══════════════════════════════════════════════════════════════════════════

import { definePage } from '../router.js';
import {
  zone,
  coloringSeverity,
  jitter,
  buildZoneColorMap,
  matrixTooltipHtml,
  cut,
  tabStyle,
  navBtnStyle,
} from './page-matrix-helpers.js';

export interface MatrixRisk {
  id: number;
  code: string;
  nom: string;
  consequence?: string | null;
  categorie: string;
  project_id?: number | null;
  probabilite_actuelle: number;
  impact_actuel: number;
  probabilite_precedente?: number | null;
  impact_precedent?: number | null;
  revision_id?: number | null;
}

interface MatrixContributingRevision {
  revision_id: number;
  project_id: number;
}

interface MatrixTab {
  key: string;
  is_current: boolean;
  date_end?: string | null;
  risks: MatrixRisk[];
  has_previous?: boolean;
  contributing_revisions: MatrixContributingRevision[];
}

interface MatrixData {
  risks: MatrixRisk[];
  has_previous?: boolean;
  projects?: Array<{ id: number; code?: string | null; name: string }>;
  revision_id?: number;
  revision_closed?: boolean;
  project_id?: number;
  contributing_revisions?: MatrixContributingRevision[];
  tabs?: MatrixTab[];
}

interface MatrixProjectInfo {
  code?: string | null;
  name: string;
}

interface MatrixOpts {
  scope?: 'project' | 'program';
  programId?: number;
  programName?: string;
  embed?: boolean;
}

declare global {
  interface Window {
    renderMatrixPage: typeof renderMatrixPage;
    wireBubbles: typeof wireBubbles;
    _svgToBase64Png: typeof _svgToBase64Png;
    showMatrixExportModal: typeof showMatrixExportModal;
    _matrixData?: MatrixData | null;
    _isMatrixEmbedded?: typeof _isMatrixEmbedded;
  }
}

let _matrixData: MatrixData | null = null;
// Fil d'ariane : si on arrive depuis la révision d'un projet (risk-table pose
// `matrix:backHash`), on garde le hash de retour pour offrir un lien « Révision »
// dans le breadcrumb. Consommé une seule fois à l'entrée de la route (pas à
// chaque re-render) pour éviter un lien périmé quand on revient par un autre
// chemin.
let _matrixRevBackHash: string | null = null;

function _consumeMatrixBackHash(): string | null {
  let raw: string | null;
  try {
    raw = sessionStorage.getItem('matrix:backHash');
    if (raw) sessionStorage.removeItem('matrix:backHash');
  } catch {
    return null;
  }
  return raw && /^#revision\/\d+$/.test(raw) ? raw : null;
}

function renderMatrixPage(
  data: MatrixData,
  project: MatrixProjectInfo,
  opts: MatrixOpts = {},
): void {
  const scope = opts.scope || 'project';
  const isEmbed = !!opts.embed;
  // Lien retour « Révision » si on vient de la révision d'un projet.
  const revLink =
    scope === 'project' && _matrixRevBackHash
      ? ` / <a href="${_matrixRevBackHash}" style="color:var(--accent);text-decoration:underline">${esc(t('matrix.breadcrumb.revision'))}</a>`
      : '';
  const breadcrumb =
    scope === 'program'
      ? `<a href="#programs" style="color:var(--accent);text-decoration:underline">${esc(t('matrix.breadcrumb.programs'))}</a> / [${esc(project.code || '?')}] ${esc(project.name)} / ${esc(t('matrix.breadcrumb.matrix'))}`
      : `<a href="#dashboard" style="color:var(--accent);text-decoration:underline">${esc(t('matrix.breadcrumb.projects'))}</a> / <a href="#project/${data.project_id}" style="color:var(--accent);text-decoration:underline">[${esc(project.code || '?')}] ${esc(project.name)}</a>${revLink} / ${esc(t('matrix.breadcrumb.matrix'))}`;
  const dlIcon = `<svg aria-hidden="true" width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:-2px;margin-right:6px;flex-shrink:0"><path d="M10 4v9"/><path d="M6 9l4 4 4-4"/><path d="M4 17h12"/></svg>`;
  // Exports enrichis « livrables » (PPTX/PNG/SVG) : gardés par exports.rich
  // (levier de palier). Masqués sinon — l'endpoint PPTX renvoie 403 de toute
  // façon. En embed (wizard support), aucun bouton n'est rendu de base.
  const canExportRich = !!(window.hasPermission && window.hasPermission('exports.rich'));
  const exportBtns =
    isEmbed || !canExportRich
      ? ''
      : scope === 'program'
        ? `<button id="mat-export-png" class="btn btn-secondary ml-auto" style="margin-right:8px;display:inline-flex;align-items:center">${dlIcon}${esc(t('matrix.btn.png'))}</button>
         <button id="mat-export-svg" class="btn btn-secondary" style="display:inline-flex;align-items:center">${dlIcon}${esc(t('matrix.btn.svg'))}</button>`
        : `<button id="mat-export-ppt" class="btn btn-primary ml-auto" style="margin-right:8px;display:inline-flex;align-items:center">${dlIcon}${esc(t('matrix.btn.exportPpt'))}</button>
         <button id="mat-export-png" class="btn btn-secondary" style="margin-right:8px;display:inline-flex;align-items:center">${dlIcon}${esc(t('matrix.btn.png'))}</button>
         <button id="mat-export-svg" class="btn btn-secondary" style="display:inline-flex;align-items:center">${dlIcon}${esc(t('matrix.btn.svg'))}</button>`;
  if (!isEmbed) setHeader(breadcrumb, exportBtns);

  const projects = data.projects || [];
  let activeProjectId: number | 'all' = 'all';

  // Onglets de révisions : 1er = état courant, suivants = instantanés
  // historiques (cf. risk_snapshots.get_aggregated_matrix). `null` si une
  // seule révision ou réponse sans onglets → comportement mono-révision.
  const tabs: MatrixTab[] | null = data.tabs && data.tabs.length ? data.tabs : null;
  let activeTabIdx = 0;
  const anyHasPrevious = tabs ? tabs.some((tb) => !!tb.has_previous) : !!data.has_previous;

  const CAT_COLORS = [
    '#4477AA',
    '#EE6677',
    '#228833',
    '#CCBB44',
    '#66CCEE',
    '#AA3377',
    '#BBBBBB',
    '#EE8866',
    '#44BB99',
    '#FFAABB',
  ];

  // Catégories = union de tous les onglets pour que les filtres restent
  // stables quand on bascule sur une révision qui a d'autres catégories.
  const _catSource = tabs ? tabs.flatMap((tb) => tb.risks) : data.risks;
  const categories = [...new Set(_catSource.map((r) => r.categorie))];
  const catColor: Record<string, string> = Object.fromEntries(
    categories.map((c, i) => [c, CAT_COLORS[i % CAT_COLORS.length] ?? '#bbbbbb']),
  );

  const PROB_POS: Record<number, number> = { 1: 0, 2: 1, 3: 2.25, 4: 3.8125 };
  const IMP_POS: Record<number, number> = { 1: 0, 2: 1, 3: 2.25, 4: 3.8125 };
  const AXIS_MAX = 3.8125;

  // Zonage / couleurs / jitter : helpers purs délégués à page-matrix-helpers.
  const zoneColor = buildZoneColorMap();
  const zoneLabel: Record<'low' | 'med' | 'high' | 'crit', string> = {
    low: t('matrix.zone.low'),
    med: t('matrix.zone.med'),
    high: t('matrix.zone.high'),
    crit: t('matrix.zone.crit'),
  };

  const activeCats = new Set<string>(categories);
  const activeCrits = new Set<string>(['low', 'med', 'high', 'crit']);
  let showArrows = true;

  function buildSVG(forExport = false): string {
    const ML = 70;
    const MR = 40;
    const MT = 36;
    const MB = 60;
    const W = 580;
    const H = 460;
    const PW = W - ML - MR;
    const PH = H - MT - MB;
    const PROB_VALS = [1, 2, 3, 4];
    const IMP_VALS = [1, 2, 3, 4];

    const sx = (p: number): number => ML + ((PROB_POS[p] ?? 0) / AXIS_MAX) * PW;
    const sy = (i: number): number => MT + (1 - (IMP_POS[i] ?? 0) / AXIS_MAX) * PH;
    const R = Math.round(0.033 * PW);

    const filtered = data.risks.filter(
      (r) =>
        (activeProjectId === 'all' || r.project_id === activeProjectId) &&
        activeCats.has(r.categorie) &&
        activeCrits.has(zone(r.probabilite_actuelle * r.impact_actuel)),
    );

    const pGrads = [1, 2, 3, 4];
    const iGrads = [1, 2, 3, 4];
    let bgRects = '';
    for (let pi = 0; pi < 3; pi++) {
      for (let ii = 0; ii < 3; ii++) {
        const pA = pGrads[pi] ?? 1;
        const pB = pGrads[pi + 1] ?? 1;
        const iA = iGrads[ii] ?? 1;
        const iB = iGrads[ii + 1] ?? 1;
        const pCenter = (pA + pB) / 2;
        const iCenter = (iA + iB) / 2;
        const cellSev = pCenter * iCenter;
        const fill = sevColor(coloringSeverity(cellSev)) + '66';
        const x1 = sx(pA);
        const x2 = sx(pB);
        const y1 = sy(iB);
        const y2 = sy(iA);
        bgRects += `<rect x="${x1.toFixed(1)}" y="${y1.toFixed(1)}" width="${(x2 - x1).toFixed(1)}" height="${(y2 - y1).toFixed(1)}" fill="${fill}"/>`;
      }
    }

    let grid = '';
    for (const p of PROB_VALS) {
      const x = sx(p);
      grid += `<line x1="${x}" y1="${MT}" x2="${x}" y2="${MT + PH}" stroke="#d1d5db" stroke-width="0.5" stroke-dasharray="3,3"/>`;
      grid += `<text x="${x}" y="${MT + PH + 18}" text-anchor="middle" font-size="12" fill="#6b7280">${p}</text>`;
    }
    for (const i of IMP_VALS) {
      const y = sy(i);
      grid += `<line x1="${ML}" y1="${y}" x2="${ML + PW}" y2="${y}" stroke="#d1d5db" stroke-width="0.5" stroke-dasharray="3,3"/>`;
      grid += `<text x="${ML - 10}" y="${y + 4}" text-anchor="end" font-size="12" fill="#6b7280">${i}</text>`;
    }
    grid += `<rect x="${ML}" y="${MT}" width="${PW}" height="${PH}" fill="none" stroke="#e5e7eb" stroke-width="1"/>`;
    grid += `<text x="${ML + PW / 2}" y="${H - 8}" text-anchor="middle" font-size="12" fill="#9ca3af" font-weight="500">${esc(t('matrix.axis.proba'))}</text>`;
    grid += `<text x="14" y="${MT + PH / 2}" text-anchor="middle" font-size="12" fill="#9ca3af" font-weight="500" transform="rotate(-90,14,${MT + PH / 2})">${esc(t('matrix.axis.impact'))}</text>`;

    const defs = `<defs>
      <marker id="ar" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto"><polygon points="0 0,7 3.5,0 7" fill="#dc2626"/></marker>
      <marker id="ag" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto"><polygon points="0 0,7 3.5,0 7" fill="#16a34a"/></marker>
      <marker id="an" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto"><polygon points="0 0,7 3.5,0 7" fill="#9ca3af"/></marker>
    </defs>`;

    let arrows = '';
    if (data.has_previous) {
      for (const r of filtered) {
        const pp = r.probabilite_precedente;
        const ip = r.impact_precedent;
        if (pp == null || ip == null) continue;
        const prevS = pp * ip;
        const currS = r.probabilite_actuelle * r.impact_actuel;
        if (Math.abs(currS - prevS) < 2 && zone(currS) === zone(prevS)) continue;
        const jx = jitter(r.id, 'x');
        const jy = jitter(r.id, 'y');
        const x1 = sx(pp) + (jx / AXIS_MAX) * PW;
        const y1 = sy(ip) - (jy / AXIS_MAX) * PH;
        const x2 = sx(r.probabilite_actuelle) + (jx / AXIS_MAX) * PW;
        const y2 = sy(r.impact_actuel) - (jy / AXIS_MAX) * PH;
        const dx = x2 - x1;
        const dy = y2 - y1;
        const len = Math.sqrt(dx * dx + dy * dy);
        if (len < 2) continue;
        const ux = dx / len;
        const uy = dy / len;
        const mid = currS > prevS ? 'ar' : currS < prevS ? 'ag' : 'an';
        const col = currS > prevS ? '#dc2626' : currS < prevS ? '#16a34a' : '#9ca3af';
        arrows += `<line x1="${(x1 + ux * R).toFixed(1)}" y1="${(y1 + uy * R).toFixed(1)}" x2="${(x2 - ux * (R + 7)).toFixed(1)}" y2="${(y2 - uy * (R + 7)).toFixed(1)}" stroke="${col}" stroke-width="1.8" marker-end="url(#${mid})" opacity="0.85"/>`;
      }
    }

    let bubbles = '';
    for (const r of filtered) {
      const jx = jitter(r.id, 'x');
      const jy = jitter(r.id, 'y');
      const cx = (sx(r.probabilite_actuelle) + (jx / AXIS_MAX) * PW).toFixed(1);
      const cy = (sy(r.impact_actuel) - (jy / AXIS_MAX) * PH).toFixed(1);
      const col = catColor[r.categorie];
      const isNew =
        data.has_previous && (r.probabilite_precedente == null || r.impact_precedent == null);
      const fcx = parseFloat(cx);
      const fcy = parseFloat(cy);
      // a11y : bulle navigable au clavier (Tab) + activable (Enter/Espace) et
      // étiquetée pour les lecteurs d'écran (l'info ne repose plus sur la
      // seule position/couleur). Le label = code + catégorie du risque.
      const bubbleLabel = `${r.code}${r.categorie ? ' — ' + r.categorie : ''}`;
      const interactive = forExport
        ? ''
        : `class="risk-bubble" data-rid="${r.id}" style="cursor:pointer" tabindex="0" role="button" aria-label="${esc(bubbleLabel)}"`;
      const codeX = isNew ? fcx + R + 3 + 26 : fcx + R + 3;
      const newTagX = fcx + R + 3;
      bubbles += `<g ${interactive}>
        <circle cx="${cx}" cy="${cy}" r="${R}" fill="${col}" fill-opacity="0.88" stroke="#fff" stroke-width="1.5"/>
        ${isNew ? `<rect x="${newTagX}" y="${fcy - R - 7}" width="22" height="11" rx="5" fill="#fde047"/><text x="${newTagX + 11}" y="${fcy - R + 1}" font-size="8" text-anchor="middle" fill="#713f12" font-weight="700">${esc(t('matrix.tag.new'))}</text>` : ''}
        <text x="${codeX}" y="${fcy - R + 2}" font-size="10" fill="#374151" font-weight="500">${esc(r.code)}</text>
      </g>`;
    }

    let legend = '';
    let lx = ML;
    const ly = MT + PH + 40;
    if (forExport) {
      for (const cat of categories) {
        legend += `<rect x="${lx}" y="${ly}" width="10" height="10" rx="2" fill="${catColor[cat]}"/>`;
        legend += `<text x="${lx + 13}" y="${ly + 9}" font-size="10" fill="#374151">${esc(cat)}</text>`;
        lx += cat.length * 6.5 + 26;
      }
    }

    const svgH = forExport ? H + (categories.length > 0 ? 28 : 0) : H;
    const arrowsShown = forExport || showArrows ? arrows : '';
    return `<svg id="matrix-svg" viewBox="0 0 ${W} ${svgH}" xmlns="http://www.w3.org/2000/svg" style="width:100%;display:block">
      ${defs}${bgRects}${grid}${arrowsShown}${bubbles}${legend}
    </svg>`;
  }

  // Tooltip pur délégué à page-matrix-helpers (zoneColor threadé explicitement).
  const tooltipHtml = (r: MatrixRisk): string => matrixTooltipHtml(r, zoneColor);

  function exportPNG(): void {
    const svg = document.getElementById('matrix-svg');
    if (!svg) return;
    const s = new XMLSerializer().serializeToString(svg);
    const img = new Image();
    const vb = (svg as unknown as SVGSVGElement).viewBox.baseVal;
    img.onload = (): void => {
      const c = document.createElement('canvas');
      c.width = vb.width * 2;
      c.height = vb.height * 2;
      c.getContext('2d')?.drawImage(img, 0, 0, c.width, c.height);
      const a = document.createElement('a');
      a.download = 'matrice-risques.png';
      a.href = c.toDataURL('image/png');
      a.click();
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(s)));
  }

  function exportSVG(): void {
    const svg = document.getElementById('matrix-svg');
    if (!svg) return;
    const s = new XMLSerializer().serializeToString(svg);
    const a = document.createElement('a');
    a.download = 'matrice-risques.svg';
    a.href = URL.createObjectURL(new Blob([s], { type: 'image/svg+xml' }));
    a.click();
  }

  // Liste des risques significatifs sous la matrice. Recalculée à chaque
  // changement d'onglet / de filtre projet via renderRiskList().
  function buildRiskListHtml(): string {
    const highRisks = [...data.risks]
      .filter(
        (r) =>
          r.probabilite_actuelle * r.impact_actuel >= 6 &&
          (activeProjectId === 'all' || r.project_id === activeProjectId),
      )
      .sort(
        (a, b) =>
          b.probabilite_actuelle * b.impact_actuel - a.probabilite_actuelle * a.impact_actuel,
      );
    if (!highRisks.length) return '';
    // Layout RefactoUX mai 2026 : la liste vit à droite du graphique (1/3 de
    // largeur). Plus de table 4-cols (trop étroit) — chaque risque devient un
    // "row card" compact avec badge sévérité, code et événement empilés.
    return `
    <div class="card" style="padding:12px 14px">
      <div class="card-title" style="margin-bottom:10px;font-size:13px">${esc(t('matrix.list.title', { plural: t('vocabulary.risk_plural') }))}</div>
      <div style="display:flex;flex-direction:column;gap:8px;max-height:480px;overflow-y:auto">
        ${highRisks
          .map((r) => {
            const s = r.probabilite_actuelle * r.impact_actuel;
            const z = zone(s);
            return `<div style="display:flex;align-items:flex-start;gap:8px;padding:6px 8px;border-radius:var(--radius);background:var(--bg2);border:1px solid var(--border)">
              <span style="flex-shrink:0;min-width:26px;padding:2px 6px;text-align:center;background:${zoneColor[z]};color:#fff;border-radius:4px;font-weight:700;font-size:12px">${s}</span>
              <div style="flex:1;min-width:0">
                <div style="display:flex;align-items:center;gap:6px;margin-bottom:2px">
                  <span style="font-family:var(--font-mono);font-size:11px;background:var(--bg3);padding:1px 6px;border-radius:4px;font-weight:600">${esc(r.code)}</span>
                  <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${catColor[r.categorie]};flex-shrink:0" title="${esc(r.categorie)}"></span>
                  <span style="font-size:11px;color:var(--text3);white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${esc(r.categorie)}</span>
                </div>
                <div style="font-size:12px;color:var(--text2);line-height:1.35">${esc(r.consequence || '—')}</div>
              </div>
            </div>`;
          })
          .join('')}
      </div>
    </div>`;
  }
  function renderRiskList(): void {
    const el = document.getElementById('mat-risk-list');
    if (el) el.innerHTML = buildRiskListHtml();
  }

  // Variante SVG de la liste des risques significatifs, destinée à l'export
  // PPTX : rasterisée en PNG via le même pipeline que le graphe (cf.
  // `_renderSvgStringToPng`), pour un rendu net et FIDÈLE à la carte in-app
  // (mêmes couleurs `zoneColor`/`catColor`, mêmes champs) — remplace l'ancien
  // dessin Pillow côté backend. Renvoie null si aucun risque significatif.
  function buildRiskListSvg(): { svg: string; w: number; h: number } | null {
    const highRisks = [...data.risks]
      .filter(
        (r) =>
          r.probabilite_actuelle * r.impact_actuel >= 6 &&
          (activeProjectId === 'all' || r.project_id === activeProjectId),
      )
      .sort(
        (a, b) =>
          b.probabilite_actuelle * b.impact_actuel - a.probabilite_actuelle * a.impact_actuel,
      );
    if (!highRisks.length) return null;
    const W = 300;
    const pad = 12;
    const headerH = 34;
    const rowH = 44;
    const MAX = 9;
    const overflow = Math.max(0, highRisks.length - MAX);
    const shown = highRisks.slice(0, MAX);
    const H = headerH + rowH * shown.length + (overflow ? 22 : 0) + pad;
    const title = esc(t('matrix.list.title', { plural: t('vocabulary.risk_plural') }));
    const rows = shown
      .map((r, i) => {
        const sev = r.probabilite_actuelle * r.impact_actuel;
        const z = zone(sev);
        const top = headerH + i * rowH;
        const bw = 26;
        const bh = 22;
        const by = top + (rowH - bh) / 2 - 2;
        const tx = pad + bw + 10;
        return (
          `<rect x="6" y="${top + 3}" width="${W - 12}" height="${rowH - 6}" rx="6" fill="#F8FAFC" stroke="#E8EBEF"/>` +
          `<rect x="${pad}" y="${by}" width="${bw}" height="${bh}" rx="4" fill="${zoneColor[z]}"/>` +
          `<text x="${pad + bw / 2}" y="${by + bh / 2 + 4}" font-size="12" font-weight="700" fill="#ffffff" text-anchor="middle">${sev}</text>` +
          `<text x="${tx}" y="${top + 19}" font-size="12" font-weight="700" fill="#001C3D">${esc(r.code)}</text>` +
          `<circle cx="${tx + 50}" cy="${top + 15}" r="4" fill="${catColor[r.categorie] || '#bbbbbb'}"/>` +
          `<text x="${tx + 60}" y="${top + 19}" font-size="11" fill="#5F6B7A">${esc(cut(r.categorie || '', 18))}</text>` +
          `<text x="${tx}" y="${top + 36}" font-size="12" fill="#3A4658">${esc(cut(r.consequence || '—', 36))}</text>`
        );
      })
      .join('');
    const overflowTxt = overflow
      ? `<text x="${pad}" y="${headerH + rowH * shown.length + 14}" font-size="11" fill="#5F6B7A">… +${overflow} ${esc(t('vocabulary.risk_plural'))}</text>`
      : '';
    const svg =
      `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}" font-family="Inter, system-ui, sans-serif">` +
      `<rect width="${W}" height="${H}" fill="#ffffff"/>` +
      `<text x="${pad}" y="22" font-size="13" font-weight="700" fill="#001C3D">${title}</text>` +
      rows +
      overflowTxt +
      `</svg>`;
    return { svg, w: W, h: H };
  }

  // ── Onglets de révisions (rendus sous le graphique) ──
  // Style « dossier » aligné sur les onglets du modal d'édition projet
  // (page-projects.ts). Flèches ‹ › pour avancer/reculer d'une révision.
  const CHEVRON_L =
    '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>';
  const CHEVRON_R =
    '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>';
  function buildTabsHtml(): string {
    if (!tabs || tabs.length < 2 || isEmbed) return '';
    const btns = tabs
      .map((tb, i) => {
        const label = tb.is_current ? t('matrix.tab.current') : fmtDate(tb.date_end);
        return `<button type="button" class="mat-tab" data-tab-idx="${i}" style="${tabStyle(i === activeTabIdx)}">${esc(label)}</button>`;
      })
      .join('');
    // RefactoUX mai 2026 : barre d'onglets remontée au-dessus du SVG pour
    // que la révision affichée soit visible AVANT le graphique (lecture
    // naturelle haut→bas). `margin-bottom` au lieu de `margin-top`.
    return `<div id="mat-tabs" style="display:flex;align-items:center;gap:8px;margin-bottom:14px">
      <button type="button" class="mat-nav" data-dir="-1" aria-label="${esc(t('matrix.tab.newer'))}" style="${navBtnStyle(false)}">${CHEVRON_L}</button>
      <div style="display:flex;gap:4px;border-bottom:1px solid var(--border);padding:0 2px;flex:1;overflow-x:auto;align-items:flex-end">${btns}</div>
      <button type="button" class="mat-nav" data-dir="1" aria-label="${esc(t('matrix.tab.older'))}" style="${navBtnStyle(false)}">${CHEVRON_R}</button>
    </div>`;
  }
  function paintTabs(): void {
    document.querySelectorAll<HTMLButtonElement>('.mat-tab').forEach((btn) => {
      const idx = parseInt(btn.dataset['tabIdx'] || '0');
      btn.setAttribute('style', tabStyle(idx === activeTabIdx));
    });
    const last = tabs ? tabs.length - 1 : 0;
    document.querySelectorAll<HTMLButtonElement>('.mat-nav').forEach((btn) => {
      const target = activeTabIdx + parseInt(btn.dataset['dir'] || '0');
      const enabled = target >= 0 && target <= last;
      btn.disabled = !enabled;
      btn.setAttribute('style', navBtnStyle(enabled));
    });
  }
  function applyTab(idx: number): void {
    if (!tabs || idx === activeTabIdx || idx < 0 || idx >= tabs.length) return;
    const tb = tabs[idx];
    if (!tb) return;
    activeTabIdx = idx;
    // Bascule l'onglet actif : les filtres (catégories, sévérité, projet,
    // flèches) restent inchangés, seules les données de la révision changent.
    data.risks = tb.risks;
    data.has_previous = !!tb.has_previous;
    data.contributing_revisions = tb.contributing_revisions;
    const cr = (tb.contributing_revisions || [])[0];
    if (cr) {
      data.revision_id = cr.revision_id;
      data.project_id = cr.project_id;
    }
    // Transition : fondu sortant de la matrice, swap du SVG, fondu entrant.
    const card = document.getElementById('mat-svg-card');
    if (card) {
      card.style.transition = 'opacity 0.16s ease';
      card.style.opacity = '0';
      window.setTimeout(() => {
        card.innerHTML = buildSVG();
        wireBubbles(tooltipHtml);
        card.style.opacity = '1';
      }, 160);
    }
    renderRiskList();
    paintTabs();
  }

  const _hashParams = ((): URLSearchParams => {
    const h = window.location.hash || '';
    const qIdx = h.indexOf('?');
    if (qIdx < 0) return new URLSearchParams();
    return new URLSearchParams(h.slice(qIdx + 1));
  })();
  const _allowedSev = ((): Set<string> | null => {
    const v = _hashParams.get('sev');
    if (!v) return null;
    return new Set(
      v
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
    );
  })();
  const _allowedCat = ((): Set<string> | null => {
    const v = _hashParams.get('cat');
    if (!v) return null;
    return new Set(
      v
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
    );
  })();
  const _isSevChecked = (z: string): boolean => (_allowedSev === null ? true : _allowedSev.has(z));
  const _isCatChecked = (c: string): boolean => (_allowedCat === null ? true : _allowedCat.has(c));

  // Build initial state from URL hash params (avoid double-init).
  if (_allowedSev !== null) {
    activeCrits.clear();
    for (const z of ['low', 'med', 'high', 'crit']) {
      if (_isSevChecked(z)) activeCrits.add(z);
    }
  }
  if (_allowedCat !== null) {
    activeCats.clear();
    for (const c of categories) {
      if (_isCatChecked(c)) activeCats.add(c);
    }
  }

  const catFiltersHtml =
    categories
      .map(
        (c) =>
          `<label style="display:flex;align-items:center;gap:5px;cursor:pointer;white-space:nowrap;font-size:12px">
      <input type="checkbox" class="mat-cf" value="${esc(c)}"${_isCatChecked(c) ? ' checked' : ''}>
      <span style="width:10px;height:10px;border-radius:2px;background:${catColor[c]};flex-shrink:0;display:inline-block"></span>
      ${esc(c)}
    </label>`,
      )
      .join('') +
    `<button id="mat-toggle-cats" class="btn btn-secondary btn-sm" style="margin-left:4px">${esc(t('matrix.filter.toggleHide'))}</button>`;

  const critFiltersHtml = ['low', 'med', 'high', 'crit']
    .map(
      (z) =>
        `<label style="display:flex;align-items:center;gap:5px;cursor:pointer;font-size:12px">
      <input type="checkbox" class="mat-zf" value="${z}"${_isSevChecked(z) ? ' checked' : ''}>
      <span style="color:${zoneColor[z]}">${zoneLabel[z as 'low' | 'med' | 'high' | 'crit']}</span>
    </label>`,
    )
    .join('');

  const projectFilterHtml =
    projects.length > 1
      ? `
    <div>
      <div style="font-size:11px;font-weight:500;color:var(--text3);text-transform:uppercase;letter-spacing:.06em;margin-bottom:6px">${esc(t('matrix.filter.project'))}</div>
      <select id="mat-project-filter" style="padding:5px 8px;font-size:12px;border:1px solid var(--border2);border-radius:var(--radius);background:var(--bg2)">
        <option value="all" selected>${esc(t('matrix.filter.allProjects'))}</option>
        ${projects.map((p) => `<option value="${p.id}">[${esc(p.code || '?')}] ${esc(p.name)}</option>`).join('')}
      </select>
    </div>`
      : '';

  // RefactoUX mai 2026 : layout 2 cols sur la zone Graphique / Liste des
  // risques significatifs (2/3 SVG · 1/3 liste). Le max-width passe à 1200px
  // pour absorber la 2e colonne sans rétrécir le SVG. La barre d'onglets et
  // le panneau de filtres restent pleine largeur au-dessus.
  const html = `
    <div style="max-width:1200px;margin:0 auto">
      <div class="card" style="margin-bottom:12px;padding:12px 16px">
        <div style="display:flex;gap:24px;flex-wrap:wrap;align-items:flex-start">
          ${projectFilterHtml}
          <div>
            <div style="font-size:11px;font-weight:500;color:var(--text3);text-transform:uppercase;letter-spacing:.06em;margin-bottom:6px">${esc(t('matrix.filter.categories'))}</div>
            <div style="display:flex;flex-wrap:wrap;gap:8px 16px">${catFiltersHtml}</div>
          </div>
          <div>
            <div style="font-size:11px;font-weight:500;color:var(--text3);text-transform:uppercase;letter-spacing:.06em;margin-bottom:6px">${esc(t('matrix.filter.severity'))}</div>
            <div style="display:flex;flex-wrap:wrap;gap:6px 14px">${critFiltersHtml}</div>
          </div>
          ${
            anyHasPrevious
              ? `
          <div>
            <div style="font-size:11px;font-weight:500;color:var(--text3);text-transform:uppercase;letter-spacing:.06em;margin-bottom:6px">${esc(t('matrix.filter.arrows'))}</div>
            <label style="display:flex;align-items:center;gap:6px;cursor:pointer;font-size:12px">
              <input type="checkbox" id="mat-show-arrows" checked>
              ${esc(t('matrix.filter.arrowsLabel'))}
            </label>
          </div>`
              : ''
          }
          <div style="font-size:11px;color:var(--text3);margin-top:auto;padding-bottom:2px">
            ${esc(anyHasPrevious ? t('matrix.note.arrows') : t('matrix.note.noPrevious'))}
          </div>
        </div>
      </div>
      ${buildTabsHtml()}
      <div style="display:grid;grid-template-columns:2fr 1fr;gap:16px;align-items:start">
        <div class="card" id="mat-svg-card">${buildSVG()}</div>
        <div id="mat-risk-list" style="min-width:0">${buildRiskListHtml()}</div>
      </div>
      <div id="mat-tooltip" style="position:fixed;display:none;background:#fff;border:0.5px solid #e5e7eb;border-radius:8px;padding:10px 12px;pointer-events:none;z-index:200;box-shadow:0 4px 12px rgba(0,0,0,.12)"></div>
    </div>`;

  setContent(html);

  document.getElementById('mat-project-filter')?.addEventListener('change', (e: Event) => {
    const target = e.target as HTMLSelectElement | null;
    activeProjectId = (target?.value || 'all') === 'all' ? 'all' : parseInt(target?.value || '0');
    const card = document.getElementById('mat-svg-card');

    if (card) card.innerHTML = buildSVG();
    wireBubbles(tooltipHtml);
    renderRiskList();
  });
  document.querySelectorAll<HTMLInputElement>('.mat-cf').forEach((cb) => {
    cb.addEventListener('change', () => {
      if (cb.checked) activeCats.add(cb.value);
      else activeCats.delete(cb.value);
      const card = document.getElementById('mat-svg-card');

      if (card) card.innerHTML = buildSVG();
      wireBubbles(tooltipHtml);
      _updateToggleCatsLabel();
    });
  });
  function _updateToggleCatsLabel(): void {
    const btn = document.getElementById('mat-toggle-cats');
    if (!btn) return;
    btn.textContent =
      activeCats.size === categories.length
        ? t('matrix.filter.toggleHide')
        : t('matrix.filter.toggleShow');
  }
  document.getElementById('mat-toggle-cats')?.addEventListener('click', () => {
    const allChecked = activeCats.size === categories.length;
    if (allChecked) {
      activeCats.clear();
      document.querySelectorAll<HTMLInputElement>('.mat-cf').forEach((cb) => {
        cb.checked = false;
      });
    } else {
      categories.forEach((c) => activeCats.add(c));
      document.querySelectorAll<HTMLInputElement>('.mat-cf').forEach((cb) => {
        cb.checked = true;
      });
    }
    const card = document.getElementById('mat-svg-card');

    if (card) card.innerHTML = buildSVG();
    wireBubbles(tooltipHtml);
    _updateToggleCatsLabel();
  });
  _updateToggleCatsLabel();
  document.querySelectorAll<HTMLInputElement>('.mat-zf').forEach((cb) => {
    cb.addEventListener('change', () => {
      if (cb.checked) activeCrits.add(cb.value);
      else activeCrits.delete(cb.value);
      const card = document.getElementById('mat-svg-card');

      if (card) card.innerHTML = buildSVG();
      wireBubbles(tooltipHtml);
    });
  });
  document.getElementById('mat-show-arrows')?.addEventListener('change', (e: Event) => {
    const target = e.target as HTMLInputElement | null;
    showArrows = !!target?.checked;
    const card = document.getElementById('mat-svg-card');

    if (card) card.innerHTML = buildSVG();
    wireBubbles(tooltipHtml);
  });

  document.querySelectorAll<HTMLButtonElement>('.mat-tab').forEach((btn) => {
    btn.addEventListener('click', () => {
      applyTab(parseInt(btn.dataset['tabIdx'] || '0'));
    });
  });
  document.querySelectorAll<HTMLButtonElement>('.mat-nav').forEach((btn) => {
    btn.addEventListener('click', () => {
      applyTab(activeTabIdx + parseInt(btn.dataset['dir'] || '0'));
    });
  });
  paintTabs();

  document.getElementById('mat-export-png')?.addEventListener('click', exportPNG);
  document.getElementById('mat-export-svg')?.addEventListener('click', exportSVG);
  document
    .getElementById('mat-export-ppt')
    ?.addEventListener('click', () => showMatrixExportModal(data, project, buildRiskListSvg()));

  wireBubbles(tooltipHtml);

  if (isEmbed) {
    // Cast `as never` car _mwExportPng a une signature `() => Promise<string>`
    // dans page-meeting-support, on retourne string|null en pratique.
    (window as Window & { _mwExportPng?: () => Promise<string> })._mwExportPng = (async () => {
      try {
        return (await _svgToBase64Png()) ?? '';
      } catch {
        return '';
      }
    }) as () => Promise<string>;
    // Liste des risques significatifs en PNG (même SVG que l'export #matrix) :
    // permet au wizard « Préparer un comité » d'embarquer la carte fidèle dans
    // ses exports PPTX/DOCX, au lieu du rendu Pillow. '' si aucun risque.
    (window as Window & { _mwRisksExportPng?: () => Promise<string> })._mwRisksExportPng =
      (async () => {
        try {
          const r = buildRiskListSvg();
          return r ? ((await _renderSvgStringToPng(r.svg, r.w, r.h)) ?? '') : '';
        } catch {
          return '';
        }
      }) as () => Promise<string>;
    (window as Window & { _mwGetFilters?: () => unknown })._mwGetFilters = (): unknown => {
      const ZONE_LABEL: Record<string, string> = {
        low: t('matrix.zoneShort.low'),
        med: t('matrix.zoneShort.med'),
        high: t('matrix.zoneShort.high'),
        crit: t('matrix.zoneShort.crit'),
      };
      const sevAll = Array.from(document.querySelectorAll<HTMLInputElement>('input.mat-zf'));
      const catAll = Array.from(document.querySelectorAll<HTMLInputElement>('input.mat-cf'));
      const severities = sevAll
        .filter((el) => el.checked)
        .map((el) => ZONE_LABEL[el.value] || el.value);
      const catSelected = catAll.filter((el) => el.checked).map((el) => el.value);
      const projSel = document.getElementById('mat-project-filter') as HTMLSelectElement | null;
      const projectFilter = projSel
        ? projSel.value === 'all'
          ? t('matrix.filter.allProjects')
          : projSel.options[projSel.selectedIndex]?.textContent || projSel.value
        : null;
      return {
        severities,
        categories: catSelected,
        severities_total: sevAll.length,
        categories_total: catAll.length,
        project_filter: projectFilter,
      };
    };
  }
}

function wireBubbles(tooltipHtml: (r: MatrixRisk) => string): void {
  const tooltip = document.getElementById('mat-tooltip');
  document.querySelectorAll<HTMLElement>('.risk-bubble').forEach((g) => {
    const rid = parseInt(g.dataset['rid'] || '0');
    g.addEventListener('mouseenter', () => {
      if (!tooltip) return;
      const r = _matrixData?.risks?.find((x) => x.id === rid);
      if (r) {
        tooltip.innerHTML = tooltipHtml(r);
        tooltip.style.display = 'block';
      }
    });
    g.addEventListener('mousemove', (e: MouseEvent) => {
      if (!tooltip) return;
      const x = e.clientX + 14;
      const tw = tooltip.offsetWidth;
      tooltip.style.left = (x + tw > window.innerWidth - 8 ? x - tw - 28 : x) + 'px';
      tooltip.style.top = Math.max(8, e.clientY - 10) + 'px';
    });
    g.addEventListener('mouseleave', () => {
      if (tooltip) tooltip.style.display = 'none';
    });
    const openRevision = (): void => {
      (ps as { expandedRiskId?: number }).expandedRiskId = rid;
      // Navigue vers la révision de l'onglet actif à laquelle ce risque
      // appartient (peut différer de la révision courante du projet).
      const rk = _matrixData?.risks?.find((x) => x.id === rid);
      router.navigate('#revision/' + (rk?.revision_id ?? _matrixData?.revision_id));
    };
    g.addEventListener('click', openRevision);
    // a11y : tooltip aussi au focus clavier (pas seulement au survol souris).
    g.addEventListener('focus', () => {
      if (!tooltip) return;
      const r = _matrixData?.risks?.find((x) => x.id === rid);
      if (!r) return;
      tooltip.innerHTML = tooltipHtml(r);
      tooltip.style.display = 'block';
      const rect = g.getBoundingClientRect();
      const tw = tooltip.offsetWidth;
      const x = rect.right + 14;
      tooltip.style.left = (x + tw > window.innerWidth - 8 ? rect.left - tw - 14 : x) + 'px';
      tooltip.style.top = Math.max(8, rect.top - 10) + 'px';
    });
    g.addEventListener('blur', () => {
      if (tooltip) tooltip.style.display = 'none';
    });
    // a11y : Enter / Espace activent la bulle comme un clic.
    g.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openRevision();
      }
    });
  });
}

function _svgToBase64Png(): Promise<string | null> {
  return new Promise((resolve) => {
    const svg = document.getElementById('matrix-svg') as unknown as SVGSVGElement | null;
    if (!svg) {
      resolve(null);
      return;
    }
    const s = new XMLSerializer().serializeToString(svg);
    const vb = svg.viewBox.baseVal;
    const img = new Image();
    img.onload = (): void => {
      const cv = document.createElement('canvas');
      cv.width = vb.width * 2;
      cv.height = vb.height * 2;
      cv.getContext('2d')?.drawImage(img, 0, 0, cv.width, cv.height);
      resolve(cv.toDataURL('image/png').split(',')[1] || null);
    };
    img.onerror = (): void => resolve(null);
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(s)));
  });
}

// Rasterise une CHAÎNE SVG arbitraire (≠ `_svgToBase64Png` qui lit le DOM) en
// PNG base64, ×2 pour la netteté. Sert à la liste des risques significatifs de
// l'export PPTX, construite en SVG hors DOM.
function _renderSvgStringToPng(svg: string, w: number, h: number): Promise<string | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = (): void => {
      const cv = document.createElement('canvas');
      cv.width = w * 2;
      cv.height = h * 2;
      cv.getContext('2d')?.drawImage(img, 0, 0, cv.width, cv.height);
      resolve(cv.toDataURL('image/png').split(',')[1] || null);
    };
    img.onerror = (): void => resolve(null);
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svg)));
  });
}

function showMatrixExportModal(
  data: MatrixData,
  project: MatrixProjectInfo,
  risksSvg?: { svg: string; w: number; h: number } | null,
): void {
  const isClosed = data.revision_closed;
  const closedWarning = isClosed
    ? `<div style="margin:8px 0;padding:8px 10px;background:#fffbeb;border:1px solid #fde68a;border-radius:6px;font-size:11px;color:#92400e">
        ${esc(t('matrix.export.warnClosed'))}
       </div>`
    : '';

  const body = `
    <div class="form-group">
      <label style="font-weight:500;font-size:13px">${esc(t('matrix.export.visibility'))}</label>
      <div style="margin-top:6px;display:flex;flex-direction:column;gap:4px">
        <label style="display:flex;align-items:center;gap:6px;font-size:13px;cursor:pointer">
          <input type="radio" name="mat-vis" value="all" checked> ${esc(t('matrix.export.allRisks'))}
        </label>
        <label style="display:flex;align-items:center;gap:6px;font-size:13px;cursor:pointer">
          <input type="radio" name="mat-vis" value="public"> ${esc(t('matrix.export.publicOnly'))}
        </label>
      </div>
    </div>
    <div class="form-group" style="margin-top:12px">
      <label style="display:flex;align-items:center;gap:8px;font-size:13px;cursor:pointer">
        <input type="checkbox" id="mat-include-actions" checked>
        ${esc(t('matrix.export.includeActions'))}
      </label>
      ${closedWarning}
    </div>`;

  openModal(
    t('matrix.export.modalTitle'),
    body,
    async () => {
      const visEl = document.querySelector<HTMLInputElement>('input[name="mat-vis"]:checked');
      const visibility = visEl?.value || 'all';
      const inclEl = document.getElementById('mat-include-actions') as HTMLInputElement | null;
      const includeActions = inclEl?.checked ?? true;

      closeModal();
      toast(t('matrix.toast.generatingPpt'));

      const matrixPng = await _svgToBase64Png();
      const risksPng = risksSvg
        ? await _renderSvgStringToPng(risksSvg.svg, risksSvg.w, risksSvg.h)
        : null;

      const payload = {
        visibility,
        include_actions: includeActions,
        matrix_png: matrixPng,
        risks_png: risksPng,
        user_id: currentUserId(),
        export_dt: new Date().toISOString(),
      };

      try {
        if (data.revision_id == null) return;
        const blob = await api.exportRevisionPptx(data.revision_id, payload);
        api.triggerBlobDownload(blob, `${project.code || 'PROJ'}_matrice.pptx`);
        toast(t('matrix.toast.pptDownloaded'));
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : t('common.error');
        toast(msg, 'error');
      }
    },
    t('matrix.export.btnDownload'),
  );
}

function _isMatrixEmbedded(): boolean {
  try {
    return !!(window.parent && window.parent !== window);
  } catch {
    return false;
  }
}

// La matrice est non vide si l'onglet courant OU un onglet historique
// porte au moins un risque (révision courante vide mais passé renseigné).
function _matrixHasAnyRisk(data: MatrixData): boolean {
  if (data.tabs && data.tabs.length) return data.tabs.some((tb) => tb.risks.length > 0);
  return !!data.risks && data.risks.length > 0;
}

pages.matrixView = definePage<'matrix/:pid'>(async ({ pid }) => {
  _matrixData = null;
  _matrixRevBackHash = _consumeMatrixBackHash();
  if (!_isMatrixEmbedded()) setHeader(t('matrix.loading'), '');
  setContent(`<div class="loading">${esc(t('matrix.loading.matrix'))}</div>`);
  try {
    const [data, project] = (await Promise.all([
      api.getAggregatedMatrix([pid]),
      api.getProject(pid),
    ])) as [MatrixData, MatrixProjectInfo];
    if (!data || !_matrixHasAnyRisk(data)) {
      setHeader(
        `<a href="#dashboard" style="color:var(--accent);text-decoration:underline">${esc(t('matrix.breadcrumb.projects'))}</a> / ${esc(t('matrix.breadcrumb.matrix'))}`,
        '',
      );
      setContent(
        `<div class="empty" style="padding:48px;text-align:center">${esc(t('matrix.empty.noRisks'))}</div>`,
      );
      // Embed (wizard support) : signale une matrice légitimement vide pour que
      // le wizard puisse passer à l'étape suivante sans erreur de capture.
      if (_isMatrixEmbedded()) (window as Window & { _mwEmpty?: boolean })._mwEmpty = true;
      return;
    }
    const cr = (data.contributing_revisions || [])[0];
    if (cr) {
      data.revision_id = cr.revision_id;
      data.project_id = cr.project_id;
    }
    _matrixData = data;
    renderMatrixPage(data, project, { scope: 'project', embed: _isMatrixEmbedded() });
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : t('common.error');
    setContent(`<div class="error">${esc(msg)}</div>`);
  }
});

pages.matrixViewProgram = definePage<'matrix/program/:gid'>(async ({ gid }) => {
  _matrixData = null;
  _matrixRevBackHash = null; // le lien « Révision » ne concerne que les projets
  if (!_isMatrixEmbedded()) setHeader(t('matrix.loading'), '');
  setContent(`<div class="loading">${esc(t('matrix.loading.program'))}</div>`);
  try {
    const programs = (await api.getPrograms()) as Array<{
      id: number;
      code?: string | null;
      name: string;
      projects?: Array<{ id: number }>;
    }>;
    const program = programs.find((p) => p.id === gid);
    if (!program) {
      setContent(`<div class="error">${esc(t('matrix.empty.programNotFound'))}</div>`);
      return;
    }
    const projectIds = (program.projects || []).map((p) => p.id);
    if (!projectIds.length) {
      setHeader(
        `<a href="#programs" style="color:var(--accent);text-decoration:underline">${esc(t('matrix.breadcrumb.programs'))}</a> / ${esc(program.name)} / ${esc(t('matrix.breadcrumb.matrix'))}`,
        '',
      );
      setContent(
        `<div class="empty" style="padding:48px;text-align:center">${esc(t('matrix.empty.programNoProjects'))}</div>`,
      );
      return;
    }
    const data = (await api.getAggregatedMatrix(projectIds, 'program')) as MatrixData;
    if (!data || !_matrixHasAnyRisk(data)) {
      setHeader(
        `<a href="#programs" style="color:var(--accent);text-decoration:underline">${esc(t('matrix.breadcrumb.programs'))}</a> / ${esc(program.name)} / ${esc(t('matrix.breadcrumb.matrix'))}`,
        '',
      );
      setContent(
        `<div class="empty" style="padding:48px;text-align:center">${esc(t('matrix.empty.programNoRisks'))}</div>`,
      );
      if (_isMatrixEmbedded()) (window as Window & { _mwEmpty?: boolean })._mwEmpty = true;
      return;
    }
    _matrixData = data;
    renderMatrixPage(
      data,
      { code: program.code ?? null, name: program.name },
      { scope: 'program', programId: gid, programName: program.name, embed: _isMatrixEmbedded() },
    );
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : t('common.error');
    setContent(`<div class="error">${esc(msg)}</div>`);
  }
});

// ── ESM globals shim (lot B.8 + S5.3) ──
window.renderMatrixPage = renderMatrixPage;
window.wireBubbles = wireBubbles;
window._svgToBase64Png = _svgToBase64Png;
window.showMatrixExportModal = showMatrixExportModal;
window._matrixData = _matrixData;
window._isMatrixEmbedded = _isMatrixEmbedded;

export {};
